/**
 * Where an unsaved form waits between one opening and the next.
 *
 * The request (2026-11-04): a form closed by a click outside it — or by X, or by "ביטול" — must open
 * again where it was left. Only a successful save starts it over. So a draft is written on every
 * change and removed only by `clearFormDraft`, which the screen calls after its mutation succeeds.
 *
 * Two layers: a Map for this page's life (it can hold `File`s, which JSON cannot), mirrored into
 * `sessionStorage` so a refresh keeps the typing too. `sessionStorage` dies with the tab, and
 * `clearAllFormDrafts` runs on logout, so a draft never outlives the person who typed it.
 *
 * Keys are dotted, `<area>.<form>[.<id>][.<piece>]`. Clearing a key clears everything under it, so a
 * form with several pieces of state (fields, files, a spec) is started over by one call.
 */

interface StoredDraft {
  value: unknown;
  /** What the form started from — a draft over a record that changed since is dropped. */
  baseline: string;
}

const PREFIX = 'formDraft:';
const memory = new Map<string, StoredDraft>();
const listeners = new Set<(key: string) => void>();

const storage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const covers = (prefix: string, key: string) => key === prefix || key.startsWith(`${prefix}.`);

export const readFormDraft = (key: string): StoredDraft | undefined => {
  const held = memory.get(key);
  if (held) return held;
  try {
    const raw = storage()?.getItem(PREFIX + key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as StoredDraft;
    memory.set(key, parsed);
    return parsed;
  } catch {
    return undefined;
  }
};

/** `memoryOnly` for values JSON would mangle (a list of `File`s): they survive closing, not a refresh. */
export const writeFormDraft = (key: string, draft: StoredDraft, memoryOnly = false) => {
  memory.set(key, draft);
  if (memoryOnly) return;
  try {
    storage()?.setItem(PREFIX + key, JSON.stringify(draft));
  } catch {
    // Full or blocked storage: the draft still lives in memory until the page is left.
  }
};

/** Forget one form's draft, without telling a form that is open on it (it is the form's own write). */
export const dropFormDraft = (key: string) => {
  memory.delete(key);
  try {
    storage()?.removeItem(PREFIX + key);
  } catch {
    // Nothing to do.
  }
};

/**
 * Start a form over: the draft under `key`, and every piece under it. Call it after a save succeeds.
 * A form open on it at that moment is reset to its empty state.
 */
export const clearFormDraft = (key: string) => {
  [...memory.keys()].filter(stored => covers(key, stored)).forEach(stored => memory.delete(stored));
  try {
    const store = storage();
    if (store) {
      const keys = Array.from({ length: store.length }, (_, index) => store.key(index) ?? '');
      keys
        .filter(stored => stored.startsWith(PREFIX) && covers(key, stored.slice(PREFIX.length)))
        .forEach(stored => store.removeItem(stored));
    }
  } catch {
    // Nothing to do.
  }
  listeners.forEach(listener => listener(key));
};

/** On logout: the next person at this computer does not inherit anybody's half-typed forms. */
export const clearAllFormDrafts = () => {
  memory.clear();
  try {
    const store = storage();
    if (!store) return;
    const keys = Array.from({ length: store.length }, (_, index) => store.key(index) ?? '');
    keys.filter(stored => stored.startsWith(PREFIX)).forEach(stored => store.removeItem(stored));
  } catch {
    // Nothing to do.
  }
};

export const subscribeFormDraft = (key: string, onCleared: () => void) => {
  const listener = (cleared: string) => {
    if (covers(cleared, key)) onCleared();
  };
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
